import React, { useState, useCallback, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DinePriceList } from 'api/types';
import DineOpsPriceListModal from 'components/modals/modules/dineops-module/DineOpsPriceListModal';
import DineOpsPriceListPricesModal from 'components/modals/modules/dineops-module/DineOpsPriceListPricesModal';
import DineOpsPriceListVersionModal from 'components/modals/modules/dineops-module/DineOpsPriceListVersionModal';

/**
 * MENU-005 — price lists (DO-1101).
 *
 * A second price for the same dish, by order channel and date. Each row is a
 * VERSION of a list: one code, many effective windows. A version in force is
 * never edited — its prices are on bills already raised — so a change is "New
 * version from a date", which closes the current one the day before. The dish's
 * own card price is the default list; it needs no row here.
 */

const CHANNEL_LABEL: Record<string, string> = { DINE_IN: 'Dine-in', ROOM_SERVICE: 'Room service', TAKEAWAY: 'Takeaway' };
const CLASS_LABEL: Record<string, string> = { IN_HOUSE: 'In-house', OUTSIDE: 'Outside', STAFF: 'Staff', COMPLIMENTARY: 'Complimentary' };

interface Filters { state?: string; channel?: string; }

const DineOpsPriceListsScreen: React.FC = () => {
  const [lists, setLists] = useState<DinePriceList[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [editing, setEditing] = useState<{ mode: 'create' | 'edit'; record: DinePriceList | null } | null>(null);
  const [pricing, setPricing] = useState<DinePriceList | null>(null);
  const [versioning, setVersioning] = useState<DinePriceList | null>(null);
  const [deleting, setDeleting] = useState<DinePriceList | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(false);
    try { setLists(await dineOpsService.getPriceLists()); }
    catch { setError(true); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return lists.filter((l) =>
      (!filters.state || l.state === filters.state)
      && (!filters.channel || l.orderChannels.length === 0 || l.orderChannels.includes(filters.channel))
      && (!q || l.code.toLowerCase().includes(q) || l.name.toLowerCase().includes(q)));
  }, [lists, searchQuery, filters]);

  const handleDelete = useCallback(async () => {
    if (!deleting) return;
    try {
      await dineOpsService.deletePriceList(deleting.id, deleting.rowVersion);
      notificationService.success('Deleted', `'${deleting.code}' (from ${deleting.validFrom.slice(0, 10)}) removed.`);
      void load();
    } catch (err: any) {
      notificationService.error('Not deleted', err?.response?.data?.message ?? 'The price list could not be deleted.');
    } finally {
      setDeleting(null);
    }
  }, [deleting, load]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => setEditing({ mode: 'create', record: null }), tooltip: 'New price list', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh', variant: 'icon' },
  ], [load]);

  const badge = (state: string) => {
    const style: React.CSSProperties = {
      fontSize: 11, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap',
      background: state === 'CURRENT' ? 'var(--color-success-bg, #dcfce7)' : state === 'UPCOMING' ? 'var(--color-warning-bg, #fef3c7)' : 'var(--bg-muted, #f1f5f9)',
      color: state === 'CURRENT' ? 'var(--color-success, #166534)' : state === 'UPCOMING' ? 'var(--color-warning, #92400e)' : 'var(--text-secondary, #64748b)',
    };
    return <span style={style}>{state === 'CURRENT' ? 'In force' : state === 'UPCOMING' ? 'Upcoming' : 'Expired'}</span>;
  };

  const columns: SyutGridColumn<DinePriceList>[] = useMemo(() => [
    { id: 'code', header: 'Code', accessorKey: 'code', enableSorting: true, size: 130 },
    { id: 'name', header: 'Price list', accessorKey: 'name', enableSorting: true, size: 200 },
    {
      id: 'orderChannels', header: 'Applies to', accessorKey: 'orderChannels', enableSorting: false, size: 220,
      cell: ({ row }) => (
        <span style={{ opacity: 0.85 }}>
          {row.original.orderChannels.length ? row.original.orderChannels.map((c) => CHANNEL_LABEL[c] ?? c).join(', ') : 'every channel'}
          {row.original.customerClasses.length ? ` · ${row.original.customerClasses.map((c) => CLASS_LABEL[c] ?? c).join(', ')}` : ''}
        </span>
      ),
    },
    {
      id: 'validFrom', header: 'Valid', accessorKey: 'validFrom', enableSorting: true, size: 200,
      cell: ({ row }) => `${row.original.validFrom.slice(0, 10)} → ${row.original.validTo ? row.original.validTo.slice(0, 10) : 'open'}`,
    },
    { id: 'priority', header: 'Priority', accessorKey: 'priority', enableSorting: true, size: 90 },
    {
      id: 'priceCount', header: 'Prices', accessorKey: 'priceCount', enableSorting: true, size: 90,
      cell: ({ row }) => (row.original.priceCount === 0 ? <span className="text-danger">none</span> : row.original.priceCount),
    },
    { id: 'state', header: 'Status', accessorKey: 'state', enableSorting: true, size: 110, cell: ({ row }) => badge(row.original.state) },
    { id: 'isActive', header: 'Active', accessorKey: 'isActive', enableSorting: true, size: 80, cell: ({ row }) => (row.original.isActive ? 'Yes' : 'No') },
  ], []);

  const getRowActions = useCallback((row: DinePriceList) => [
    { id: 'prices', label: row.state === 'UPCOMING' ? 'Prices — the amounts per dish' : 'Prices (read-only — in force)', icon: <IconSystem.list size={16} />, onClick: () => setPricing(row) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => setEditing({ mode: 'edit', record: row }) },
    { id: 'version', label: 'New version from a date', icon: <IconSystem.add size={16} />, onClick: () => setVersioning(row) },
    { id: 'delete', label: row.state === 'UPCOMING' ? 'Delete' : 'Delete (in force — refused)', icon: <IconSystem.trash size={16} />, onClick: () => setDeleting(row), variant: 'danger' as const },
  ], []);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Price Lists"
        description="A second price for the same dish, by order channel and date — the card price is the default"
        icon={<IconSystem.tag size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search price lists' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      <div className="module-content">
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <div style={{ padding: '8px 12px', fontSize: 12, opacity: 0.7 }}>
              Each row is a version. A version in force is never edited — add the next version from a later date; the old one closes the day before, and last week's bills keep last week's price.
            </div>
            <SyutGrid
              data={rows}
              columns={columns}
              enableSorting
              enablePagination
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={error ? 'The price lists could not be loaded.' : 'No price lists. Every dish sells at its card price. Use + to add one.'}
            />
          </div>

          <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)}><IconSystem.x size={16} /></button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Status" options={[{ value: '', label: 'Any' }, { value: 'CURRENT', label: 'In force' }, { value: 'UPCOMING', label: 'Upcoming' }, { value: 'EXPIRED', label: 'Expired' }]}
                  value={filters.state || ''} onChange={(v) => setFilters((p) => ({ ...p, state: (v as string) || undefined }))} placeholder="Any" size="sm" isClearable />
              </div>
              <div className="filter-section">
                <SyutSelect label="Channel" options={[{ value: '', label: 'Any' }, ...Object.entries(CHANNEL_LABEL).map(([v, l]) => ({ value: v, label: l }))]}
                  value={filters.channel || ''} onChange={(v) => setFilters((p) => ({ ...p, channel: (v as string) || undefined }))} placeholder="Any" size="sm" isClearable />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <DineOpsPriceListModal
          isOpen
          mode={editing.mode}
          record={editing.record}
          onSaved={(saved) => {
            const wasCreate = editing.mode === 'create';
            setEditing(null);
            void load();
            // Step 2 without hunting for it: a new list opens on its prices.
            if (wasCreate) setPricing(saved);
          }}
          onClose={() => setEditing(null)}
        />
      )}
      {pricing && <DineOpsPriceListPricesModal isOpen list={pricing} onSaved={() => { setPricing(null); void load(); }} onClose={() => setPricing(null)} />}
      {versioning && <DineOpsPriceListVersionModal isOpen list={versioning} onSaved={() => { setVersioning(null); void load(); }} onClose={() => setVersioning(null)} />}

      {deleting && (
        <ConfirmDialog
          open
          title="Delete this version?"
          message={deleting.state === 'UPCOMING'
            ? `'${deleting.code}' from ${deleting.validFrom.slice(0, 10)} has not started; it and its ${deleting.priceCount} price(s) are removed.`
            : `'${deleting.code}' has been in force. It stays on record (bills point at it); the API will refuse. Switch it off or close it with an end date instead.`}
          confirmLabel="Delete"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={handleDelete}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
};

export default DineOpsPriceListsScreen;
